import { date, Screen } from 'quasar'
import { imgixHost, isNative } from 'src/config'
import GlobalWidgetBlock from 'src/models/global-widget/GlobalWidgetBlock'
import Model, { ID, IsoDateTime, LocalizedJsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import Page from 'src/models/page/Page'

const { formatDate } = date

export const GLOBAL_WIDGET_TYPES = {
  BANNER: 'Banner',
  FOOTER: 'Footer'
}
GLOBAL_WIDGET_TYPES.ALL = _.values(GLOBAL_WIDGET_TYPES)
Object.freeze(GLOBAL_WIDGET_TYPES)

export const VISIBILITY_OPTIONS = {
  DESKTOP_WEB: 'desktop-web',
  MOBILE_WEB: 'mobile-web',
  MOBILE_APP: 'mobile-app'
}
VISIBILITY_OPTIONS.ALL = _.values(VISIBILITY_OPTIONS)
VISIBILITY_OPTIONS.WEB_ONLY = [VISIBILITY_OPTIONS.DESKTOP_WEB, VISIBILITY_OPTIONS.MOBILE_WEB]
Object.freeze(VISIBILITY_OPTIONS)

export const RESTORATION_FREQUENCY_OPTIONS = {
  EVERY_SESSION: 'Every browser session',
  EVERY_DAY: 'Every day',
  EVERY_WEEK: 'Every week',
  EVERY_MONTH: 'Every month',
  EVERY_YEAR: 'Every year'
}
RESTORATION_FREQUENCY_OPTIONS.ALL = _.values(RESTORATION_FREQUENCY_OPTIONS)
Object.freeze(RESTORATION_FREQUENCY_OPTIONS)

export const TEXT_ALIGN_OPTIONS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right'
}
TEXT_ALIGN_OPTIONS.ALL = _.values(TEXT_ALIGN_OPTIONS)
Object.freeze(TEXT_ALIGN_OPTIONS)

export const LINK_TYPE_OPTIONS = {
  MOVIE: 'Movie',
  PAGE: 'Page',
  URL: 'URL'
}
LINK_TYPE_OPTIONS.ALL = _.values(LINK_TYPE_OPTIONS)
Object.freeze(LINK_TYPE_OPTIONS)

export const WIDTH_MODE_OPTIONS = {
  CENTERED: 'Centered',
  FULL_WIDTH: 'Full Width'
}
WIDTH_MODE_OPTIONS.ALL = _.values(WIDTH_MODE_OPTIONS)
Object.freeze(WIDTH_MODE_OPTIONS)

export const BANNER_POSITION_OPTIONS = {
  TOP: 'Top of Page',
  BELOW_HEADER: 'Below Header',
  BOTTOM: 'Bottom of Page'
}
BANNER_POSITION_OPTIONS.ALL = _.values(BANNER_POSITION_OPTIONS)
Object.freeze(LINK_TYPE_OPTIONS)

export const verticalAlignOptions = [
  { value: 'start', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'end', label: 'Bottom' }
]

export default class GlobalWidget extends Model {
  static name = 'GlobalWidget'

  static level = MODEL_LEVEL.SITE

  static paginate = true
  static deletable = true
  static duplicatable = true
  static versioned = true
  static publishable = true
  static isConfiguration = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.SiteContent]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      type: {
        type: String,
        default: GLOBAL_WIDGET_TYPES.BANNER,
        options: GLOBAL_WIDGET_TYPES.ALL
      },
      name: {
        type: String,
        default: null,
        translatable: true
      },
      visiblePages: {
        type: [Page],
        output: true
      },
      visiblePageIds: {
        type: [ID],
        default: () => ([])
      },
      config: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            on: true,
            startDate: null,
            endDate: null,
            daysOfWeek: Model.configuration.currentCircuit.daysOfWeekNumbers,
            visibility: 'Both', // Both, Desktop Only, Mobile Only
            visible_for: VISIBILITY_OPTIONS.ALL,
            isDismissable: true,
            restorationFrequency: RESTORATION_FREQUENCY_OPTIONS.EVERY_SESSION,

            pageRestrictionsEnabled: false,
            pageRestrictionsExclude: false,

            style: '',
            class: '',

            denseLayout: false,
            animated: false,

            widgetPosition: BANNER_POSITION_OPTIONS.BELOW_HEADER,
            bannerBackgroundColor: 'warning',

            isSticky: false,
            bannerIsFullWidth: false,

            widthMode: WIDTH_MODE_OPTIONS.CENTERED,
            marginTop: null,
            marginBottom: null,
            marginSides: null,
            backgroundColor: GLOBAL_WIDGET_TYPES.BANNER ? 'warning' : 'dark',
            backgroundColorHex: '#000000',
            backgroundImage: null,
            backgroundImageFit: null,
            backgroundImagePosition: null,
            backgroundRepeat: null,
            backgroundAttachment: null,

            footerVerticalAlignment: 'start',
            footerHorizontalAlignment: TEXT_ALIGN_OPTIONS.LEFT,
            footerHorizontalMobileAlignment: TEXT_ALIGN_OPTIONS.CENTER,
            footerShowCircuitName: false
          }
        },
        translatables: []
      },
      content: {
        type: String,
        default: '',
        excludeFromDefault: true,
        translatable: true
      },
      globalWidgetBlocks: {
        type: [GlobalWidgetBlock],
        default: () => ([]),
        allowDestroy: true,
        acceptsNestedAttributes: true
      },
      styles: {
        type: String,
        default: '',
        excludeFromDefault: true
      },
      transformedStyles: {
        type: String,
        excludeFromDefault: true,
        output: true
      },
      scopeClass: {
        type: String,
        excludeFromDefault: true,
        output: true
      },
      publishedAt: {
        type: IsoDateTime,
        output: true
      },
      action: {
        type: String,
        default: null,
        input: true
      }
    }

    this.computed = {
      published () {
        return this.isUnarchived && !_.isNil(this.publishedAt)
      },
      current () {
        const date = this.configuration.currentSite.siteDate
        const dayOfWeek = this.configuration.currentSite.siteDayNumber

        return _.includes(this.config.daysOfWeek, dayOfWeek) &&
          (_.isEmpty(this.config.startDate) || date >= this.config.startDate.toString()) &&
          (_.isEmpty(this.config.endDate) || date <= this.config.endDate.toString())
      },
      visible () {
        return this.published && this.config.on && this.current
      },
      backgroundImageUrl () {
        if (_.isEmpty(this.config.backgroundImage)) return null

        return `${imgixHost}/${this.config.backgroundImage}?auto=format,compress`
      }
    }
  }

  get visibleFor () {
    return _(this.config.visible_for).keyBy().mapValues(_ => true).value()
  }

  get desktop () {
    return this.visibleFor[VISIBILITY_OPTIONS.DESKTOP_WEB]
  }

  get mobile () {
    return this.visibleFor[VISIBILITY_OPTIONS.MOBILE_WEB]
  }

  get app () {
    return this.visibleFor[VISIBILITY_OPTIONS.MOBILE_APP]
  }

  get desktopOnly () {
    return this.desktop && !this.mobile && !this.app
  }

  get class () {
    const classes = []

    if (this.config.class) {
      classes.push(this.config.class)
    }

    if (this.scopeClass) {
      classes.push(this.scopeClass)
    }

    if (!_.isEmpty(this.config.marginTop)) {
      classes.push('q-pt-' + this.config.marginTop)
    }

    if (!_.isEmpty(this.config.marginBottom)) {
      classes.push('q-pb-' + this.config.marginBottom)
    }

    if (!_.isEmpty(this.config.marginSides)) {
      classes.push('q-px-' + this.config.marginSides)
    }

    if (!_.isEmpty(this.config.marginSides)) {
      classes.push('q-px-' + this.config.marginSides)
    }

    if (!_.isEmpty(this.config.backgroundColor) && this.config.backgroundColor !== 'custom') {
      classes.push('bg-' + this.config.backgroundColor)
    }

    return classes.join(' ')
  }

  get style () {
    if (this.config.widthMode !== WIDTH_MODE_OPTIONS.CENTERED) return ''

    let maxWidth = ''

    if (Screen.xl) maxWidth = '1600px'
    else if (Screen.lg) maxWidth = '1360px'
    else if (Screen.md) maxWidth = '1230px'
    else if (Screen.sm) maxWidth = '980px'

    return maxWidth
      ? `max-width: ${maxWidth}; margin-left: auto; margin-right: auto`
      : ''
  }

  get backgroundStyle () {
    const backgroundStyle = []
    if (this.config.backgroundColor === 'custom' && this.config.backgroundColorHex) {
      backgroundStyle.push('background-color: ' + this.config.backgroundColorHex)
    }
    if (!_.isEmpty(this.config.backgroundImage)) {
      backgroundStyle.push('background-image: ' + `url(${this.backgroundImageUrl})`)
    }
    if (!_.isEmpty(this.config.backgroundImageFit)) {
      backgroundStyle.push('background-size: ' + this.config.backgroundImageFit)
    }
    if (!_.isEmpty(this.config.backgroundRepeat)) {
      backgroundStyle.push('background-repeat: ' + this.config.backgroundRepeat)
    }
    if (!_.isEmpty(this.config.backgroundImagePosition)) {
      backgroundStyle.push('background-position: ' + this.config.backgroundImagePosition)
    }
    if (!_.isEmpty(this.config.backgroundAttachment)) {
      backgroundStyle.push('background-attachment: ' + this.config.backgroundAttachment)
    }
    backgroundStyle.push(this.config.style)
    return backgroundStyle.join(';')
  }

  get displayIcons () {
    const icons = []

    if (!this.config.on) {
      icons.push({
        name: 'toggle_off',
        color: 'grey',
        tooltip: 'Not Visible',
        handleClick: () => this.setVisible()
      })
    }

    if (this.config.on) {
      if (this.visible) {
        icons.push({
          name: 'visibility',
          color: 'positive',
          tooltip: 'Visible',
          handleClick: () => this.setNotVisible()
        })
      } else {
        icons.push({
          name: 'visibility_off',
          color: 'negative',
          tooltip: 'Hidden',
          handleClick: () => this.setNotVisible()
        })
      }

      if (this.config.startDate || this.config.endDate) {
        const now = this.configuration.currentSite.siteTime
        const startDate = this.config.startDate?.inTimeZone(this.configuration.currentSite.timeZone)
        const endDate = this.config.endDate?.inTimeZone(this.configuration.currentSite.timeZone)

        if (startDate && now <= startDate) {
          icons.push({
            name: 'schedule',
            color: 'positive',
            tooltip: `Starts on ${formatDate(startDate, 'MMM D, YYYY')} (${startDate.relativeTime()})`
          })
        } else if (endDate && now < endDate) {
          icons.push({
            name: 'schedule',
            color: 'negative',
            tooltip: `Ends on ${formatDate(endDate, 'MMM D, YYYY')} (${endDate.relativeTime()})`
          })
        } else if (endDate && now > endDate) {
          icons.push({
            name: 'schedule',
            color: 'grey',
            tooltip: `Ended on ${formatDate(endDate, 'MMM D, YYYY')} (${endDate.relativeTime()})`
          })
        }
      } else {
        icons.push({
          name: 'schedule',
          color: 'grey',
          tooltip: 'Not Scheduled, Always Visible'
        })
      }
    }

    if (this.mobile) {
      icons.push({
        name: 'smartphone',
        color: 'dark',
        tooltip: 'Visible on Mobile Web'
      })
    }

    if (this.desktop) {
      icons.push({
        name: 'computer',
        color: 'dark',
        tooltip: 'Visible on Desktop'
      })
    }

    if (this.app) {
      icons.push({
        name: 'fas fa-grid-2-plus',
        color: 'dark',
        tooltip: 'Visible on Mobile App'
      })
    }

    return icons
  }

  showOnScreen (screen) {
    return (this.mobile && screen.lt.sm) ||
      (this.desktop && screen.gt.xs) ||
      (this.app && isNative)
  }

  async setVisible () {
    this.config.on = true
    await this.save()
  }

  async setNotVisible () {
    this.config.on = false
    await this.save()
  }

  async publish () {
    this.action = 'publish'

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async unpublish () {
    this.action = 'unpublish'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
